import React, { useContext, useEffect, useState } from "react";
import { FiltersContext, AuthContext, AppContext } from "@CONTEXTS";
import useLogics from "./logics";
import { useGET } from "@API2";
import {
  ProductEndpoints,
  deleteProduct,
  deleteAllProducts,
  getAllProducts,
} from "@PRODUCTS";
import { addProductToDeliveryForm } from "@HELPERS";
import {
  Table,
  Row,
  Col,
  Button,
  Modal,
  message,
  Popconfirm,
  Dropdown,
  Menu,
} from "antd";
import Filters from "./filters";
import {
  AiOutlinePlus,
  AiOutlineCloudUpload,
  AiFillDelete,
  AiOutlineUserAdd,
  AiOutlineFileExcel,
  AiOutlineDownload,
} from "react-icons/ai";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const TableModal = ({
  fieldName,
  form,
  onClose,
  setStep,
  setUpdateValues,
  isReturned,
  setDeliveryType,
  setDesi,
}) => {
  const { pagination, setPageAndLimit, filters } = useContext(FiltersContext);
  const { info } = useContext(AuthContext);
  const { options } = useContext(AppContext);
  const desiOptions = isReturned
    ? options["DESI_RETURNED_TMH"]
    : options["DESI_TMH"];
  const [loading, setLoading] = useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const {
    data = [],
    loading: apiLoading,
    error,
    mutate,
  } = useGET(
    `${ProductEndpoints.listProducts}`,
    filters
      ? `?page=${pagination.page}&limit=${pagination.limit}&search=${
          filters.search ? filters.search : ""
        }`
      : false
  );
  const logics = useLogics({
    onLoading: (val) => setLoading(val),
    mutate,
    fieldName: fieldName,
    form: form,
    onClose: onClose,
    setStep: setStep,
    setUpdateValues: setUpdateValues,
    isReturned: isReturned,
    setDeliveryType,
    setDesi,
  });

  useEffect(() => {
    if (!apiLoading) {
      setLoading(false);
    }
  }, [apiLoading, data]);

  const deleteAll = async () => {
    try {
      setLoading(true);
      const response = await deleteAllProducts();
      if (response.success) {
        mutate();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const deleteSelected = async () => {
    try {
      setLoading(true);
      const response = await deleteProduct(
        selectedRows.map((x) => {
          return x.id;
        })
      );
      if (response.success) {
        mutate();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  const mapProductToExcelRow = (row) => {
    const baseRow = {
      Kod: row.code || "",
      Ad: row.name || "",
    };

    if (info?.desiCalculationMode === "DESI_ONLY") {
      return {
        ...baseRow,
        Desi: row.desi ?? "",
        Ağırlık: row.weight ?? "",
      };
    }

    return {
      ...baseRow,
      En: row.width ?? "",
      Boy: row.length ?? "",
      Yükseklik: row.height ?? "",
      Ağırlık: row.weight ?? "",
    };
  };

  const exportToExcel = () => {
    if (!info.authorities.includes("DOWNLOAD_PRODUCT")) {
      return message.error("Ürün bilgisi indirme yetkiniz bulunmamaktadır!");
    }
    if (selectedRows.length === 0) return;

    const dataToExport = selectedRows.map(mapProductToExcelRow);

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Seçilen Kayıtlar");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    saveAs(blob, "secili_urunler.xlsx");
  };

  const exportToExcelAll = async () => {
    try {
      if (!info.authorities.includes("DOWNLOAD_PRODUCT")) {
        return message.error("Ürün bilgisi indirme yetkiniz bulunmamaktadır!");
      }
      const response = await getAllProducts();
      const allData = response || [];

      if (allData.length === 0) return;

      const dataToExport = allData.map(mapProductToExcelRow);

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Tüm Kayıtlar");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });

      saveAs(blob, "tum_urunler.xlsx");
    } catch (error) {
      console.error("Excel verisi alınamadı:", error);
    }
  };

  const handleUseProduct = (record) => {
    addProductToDeliveryForm({
      form,
      fieldName,
      record,
      desiOptions: desiOptions,
      setDeliveryType,
      setDesi,
      syncTotalParcels: info?.specialTags?.includes("B2BPRO"),
    });
    onClose();
  };

  const handleAddSelectedProducts = () => {
    if (selectedRows.length === 0) {
      return;
    }

    selectedRows.forEach((record) => {
      addProductToDeliveryForm({
        form,
        fieldName,
        record,
        desiOptions: desiOptions,
        setDeliveryType,
        setDesi,
        syncTotalParcels: info?.specialTags?.includes("B2BPRO"),
      });
    });

    setSelectedRowKeys([]);
    setSelectedRows([]);
    onClose();
  };

  const menu = (
    <Menu>
      {info.authorities.includes("PRODUCT_CREATE") && (
        <Menu.Item
          key="upload"
          icon={<AiOutlineCloudUpload style={{ marginRight: 5 }} />}
          onClick={() => {
            setStep("UPLOAD");
          }}
        >
          <span>Excel ile Yükle</span>
        </Menu.Item>
      )}
      {info.authorities.includes("DOWNLOAD_PRODUCT") && (
        <>
          {" "}
          <Menu.Item
            key="exportSelected"
            icon={<AiOutlineFileExcel style={{ marginRight: 5 }} />}
            disabled={selectedRowKeys.length === 0}
            onClick={selectedRowKeys.length > 0 ? exportToExcel : undefined}
          >
            <span>
              Seçilenleri Excel Olarak İndir ({selectedRowKeys.length})
            </span>
          </Menu.Item>
          <Menu.Item
            key="exportAll"
            icon={<AiOutlineDownload style={{ marginRight: 5 }} />}
            onClick={exportToExcelAll}
          >
            <span>Tümünü Excel Olarak İndir ({data?.totalRowCounts ?? 0})</span>
          </Menu.Item>
        </>
      )}
      {info.authorities.includes("PRODUCT_DELETE") && (
        <>
          <Menu.Item
            key="deleteSelected"
            icon={
              <AiFillDelete
                style={{
                  marginRight: 5,
                  color: selectedRowKeys.length === 0 ? "#999" : "red",
                }}
              />
            }
            disabled={selectedRowKeys.length === 0}
          >
            <Popconfirm
              title={`${selectedRowKeys.length} kayıt silinecek. Onaylıyor musunuz?`}
              okText="Evet"
              cancelText="Hayır"
              onConfirm={deleteSelected}
              okType="danger"
              disabled={selectedRowKeys.length === 0}
            >
              <span>Seçilenleri Sil ({selectedRowKeys.length})</span>
            </Popconfirm>
          </Menu.Item>

          <Menu.Item
            key="deleteAll"
            icon={<AiFillDelete style={{ marginRight: 5, color: "red" }} />}
          >
            <Popconfirm
              title={`${data?.totalRowCounts} kayıt silinecek. Onaylıyor musunuz?`}
              okText="Evet"
              cancelText="Hayır"
              onConfirm={deleteAll}
              okType="danger"
            >
              <span>Tümünü Sil ({data?.totalRowCounts ?? 0})</span>
            </Popconfirm>
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  return (
    <>
      <Modal
        visible
        title="Kayıtlı Malzeme / Ürünlerim"
        onCancel={onClose}
        width={1350}
        footer={false}
      >
        <Row gutter={24}>
          <Col span={24}>
            <div className="app-content-header">
              <div
                style={{
                  width: "100%",
                  padding: "12px 24px",
                  justifyContent: "space-between",
                  display: "flex",
                }}
              >
                <Filters />
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    icon={<AiOutlineUserAdd />}
                    type="primary"
                    disabled={selectedRowKeys.length === 0}
                    onClick={handleAddSelectedProducts}
                  >
                    Seçilenleri Gönderiye Ekle ({selectedRowKeys.length})
                  </Button>
                  <Dropdown overlay={menu}>
                    <Button type="primary">
                      <span>Malzeme Listesini Yönet</span>
                    </Button>
                  </Dropdown>
                </div>
              </div>
            </div>
          </Col>
          <Col span={24}>
            <div className="receiver-page-content">
              <div className="app-deliveries">
                <Table
                  name="Ürün/Malzeme Listesi"
                  {...logics}
                  dataSource={data?.rows || []}
                  rowKey="id"
                  loading={loading}
                  scroll={{ x: "max-content", y: window.innerHeight - 600 }}
                  mutate={() => mutate()}
                  pagination={{
                    pageSizeOptions: [25, 50, 100, 250],
                    total: data?.totalRowCounts,
                    pageSize: pagination?.limit,
                    current: pagination?.page,
                    onChange: (page, limit) => {
                      setPageAndLimit({ page, limit });
                    },
                    showSizeChanger: true,
                  }}
                  onRow={(record) => {
                    return {
                      onDoubleClick: () => handleUseProduct(record),
                    };
                  }}
                  rowSelection={{
                    selectedRowKeys,
                    onChange: (selectedKeys, selectedRows) => {
                      setSelectedRowKeys(selectedKeys);
                      setSelectedRows(selectedRows);
                    },
                  }}
                />
              </div>
            </div>
          </Col>
        </Row>
        {info.authorities.includes("PRODUCT_CREATE") && (
          <Row gutter={24}>
            <Col span={24}>
              <div
                style={{
                  marginTop: 24,
                  marginBottom: 16,
                  marginLeft: 50,
                  marginRight: 50,
                }}
              >
                <Button
                  type="dashed"
                  size="large"
                  block
                  icon={<AiOutlinePlus />}
                  onClick={() => {
                    setStep("FORM");
                  }}
                >
                  Yeni Malzeme/Ürün Ekle
                </Button>
              </div>
            </Col>
          </Row>
        )}
      </Modal>
    </>
  );
};

export default TableModal;
